import {
  Stack,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Checkbox,
  Paper,
  Pagination,
  TableSortLabel,
  SxProps,
} from "@mui/material";
import RepartitionOutlinedIcon from "@mui/icons-material/RepartitionOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  setOpenBulkActionModalForCampaign,
  setOpenMoveAnotherCampaignModal,
  setPage,
  setRowsPerPage,
  setSelectedRowsForNegativesCampaign,
  setSingleRowIDPassToModalForCampaign,
  setSort,
} from "../../../../../../../../../../features/scopesTabSlice";
import NegativeSearchCampaignFilterCard from "./NegativeSearchCampaignFilterCard";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Theme } from "@emotion/react";
import { formatNumber } from "../../../../../../../../../../utils/helpers";

const NegativesInCampaignTable = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    negativesInTheCampaignData,
    selectedNegativeSearchTermForCampaign,
    selectedCampaignsForCampaign,
    selectedAdgroupsForCampaign,
    selectedRowsForNegativesCampaign,
    sort,
    rowsPerPage,
    page,
  } = useSelector((state: any) => state.searchTermOperation);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);

  // --- Table columns ---
  const tableHeaders = [
    { id: 1, KEY: "search_term", label: "Search Term", sortable: true },
    {
      id: 2,
      KEY: "negative_term_match_type",
      label: "Match Type",
      sortable: true,
    },
    { id: 3, KEY: "campaign_id", label: "Campaign ID", sortable: true },
    { id: 4, KEY: "campaign_name", label: "Campaign Name", sortable: true },
    { id: 5, KEY: "last_seen", label: "Last Seen", sortable: true },
    {
      id: 6,
      KEY: "impression_last_3_years",
      label: " IMP LAST 3 YEARS",
      sortable: true,
    },
    {
      id: 7,
      KEY: "clicks_last_3_years",
      label: "CLICKS LAST 3 YEARS",
      sortable: true,
    },
    { id: 8, KEY: "actions", label: "Actions", sortable: false },
  ];

  // --- Filter logic ---
  const filteredRows = useMemo(() => {
    return (sortedData || []).filter((row: any) => {
      const campaignMatch =
        selectedCampaignsForCampaign.length > 0
          ? selectedCampaignsForCampaign.includes(row.campaign_name)
          : true;

      const adgroupMatch =
        selectedAdgroupsForCampaign.length > 0
          ? selectedAdgroupsForCampaign.includes(row.adgroup_name)
          : true;

      const searchMatch = selectedNegativeSearchTermForCampaign
        ? row.search_term
            ?.toLowerCase()
            .includes(selectedNegativeSearchTermForCampaign?.toLowerCase())
        : true;

      return campaignMatch && adgroupMatch && searchMatch;
    });
  }, [
    sortedData,
    selectedCampaignsForCampaign,
    selectedAdgroupsForCampaign,
    selectedNegativeSearchTermForCampaign,
  ]);

  //-- Sorting Data
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      //default sorting with hidden score asc
      if (!sort.keytoSort || !sort.direction) {
        // Default sorting by modified_at (To make new items appear first in your table when they are Created or Added)
        return arrayToSort;
      }

      // Custom sort based on key and direction
      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue = a[keytoSort];
        let bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  useEffect(() => {
    if (!negativesInTheCampaignData) return;
    const modifiedWithUniqueIds = negativesInTheCampaignData.map(
      (row: any) => ({
        ...row,
        id: `${uuidv4()}`,
      })
    );
    const sortedRows = getSortedData(modifiedWithUniqueIds || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [negativesInTheCampaignData, getSortedData]);

  // --- Handle Sync selection with filtered data ---
  useEffect(() => {
    if (selectedRowIds.length === 0) return;

    // Keep only selected rows that still exist in filtered results
    const visibleSelectedIds = selectedRowIds.filter((id) =>
      filteredRows.some((r) => r.id === id)
    );

    if (visibleSelectedIds.length > 0) {
      if (visibleSelectedIds.length !== selectedRowIds.length) {
        setSelectedRowIds(visibleSelectedIds);
        const visibleSelectedRows = filteredRows.filter((r) =>
          visibleSelectedIds.includes(r.id)
        );
        dispatch(setSelectedRowsForNegativesCampaign(visibleSelectedRows));
      }
    } else {
      // No matching selected rows → clear all
      setSelectedRowIds([]);
      dispatch(setSelectedRowsForNegativesCampaign([]));
    }
    // eslint-disable-next-line
  }, [filteredRows]);

  // --- Pagination ---
  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  // --- Handle selection ---
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = paginatedRows.map((row: any) => row.id);
      setSelectedRowIds(newSelected);
      dispatch(setSelectedRowsForNegativesCampaign(paginatedRows));
    } else {
      setSelectedRowIds([]);
      dispatch(setSelectedRowsForNegativesCampaign([]));
    }
  };

  // -- Function to handle row selection
  const handleSelectRow = (id: number) => {
    const selectedIndex = selectedRowIds.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedRowIds, id];
    } else {
      newSelected = selectedRowIds.filter((rowId) => rowId !== id);
    }

    setSelectedRowIds(newSelected);

    // Update Redux selected rows
    const selectedRows = filteredRows.filter((row: any) =>
      newSelected.includes(row.id)
    );
    dispatch(setSelectedRowsForNegativesCampaign(selectedRows));
  };

  // -- Function to handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));
    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("page", newPage.toString());
    setSearchParams(updatedParams);
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForNegativesCampaign([]));
  };

  //-- Function to handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForNegativesCampaign([]));
  };

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(
      setSort({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  useEffect(() => {
    dispatch(setPage(0));
    // eslint-disable-next-line
  }, [
    selectedCampaignsForCampaign,
    selectedAdgroupsForCampaign,
    selectedNegativeSearchTermForCampaign,
  ]);

  useEffect(() => {
    dispatch(setSelectedRowsForNegativesCampaign([]));
    setSelectedRowIds([]);
    dispatch(setPage(Number(searchParams.get("page")) || 0));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
    // eslint-disable-next-line
  }, [searchParams]);

  // --- Clear selection when Redux is cleared (after API action) ---
  useEffect(() => {
    if (selectedRowsForNegativesCampaign?.length === 0) {
      setSelectedRowIds([]);
    }
  }, [selectedRowsForNegativesCampaign]);

  return (
    <>
      <NegativeSearchCampaignFilterCard />

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          mt: 2,
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 5,
                  backgroundColor: "background.paper",
                }}
              >
                <Checkbox
                  color="secondary"
                  indeterminate={
                    selectedRowIds.length > 0 &&
                    selectedRowIds.length < paginatedRows.length
                  }
                  checked={
                    paginatedRows.length > 0 &&
                    selectedRowIds.length === paginatedRows.length &&
                    selectedRowsForNegativesCampaign.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              {tableHeaders.map((header: any) => {
                // Which columns should be sticky
                const isLeftSticky = header.id === 1 || header.id === 2;
                const isRightSticky =
                  header.id === tableHeaders[tableHeaders.length - 1].id; // last column

                const leftOffsets: Record<number, number> = {
                  1: 52,
                  2: 162,
                }; //test

                const cellSx = isLeftSticky
                  ? ({
                      position: "sticky",
                      left: leftOffsets[header.id] ?? 0,
                      zIndex: 10,
                      backgroundColor: "background.paper",
                      borderRight:
                        header.id === 2 ? "1px solid #e0e0e0" : undefined,
                    } as SxProps<Theme>)
                  : isRightSticky
                  ? ({
                      position: "sticky",
                      right: 0,
                      zIndex: 9,
                      backgroundColor: "background.paper",
                      borderLeft: "1px solid #e0e0e0",
                    } as SxProps<Theme>)
                  : ({} as SxProps<Theme>);

                return (
                  <TableCell key={header.id} align="left" sx={cellSx}>
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={
                        sort.keytoSort === header.KEY ? sort.direction : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.length > 0 ? (
              paginatedRows.map((row: any) => {
                const isSelected = selectedRowIds.includes(row.id);
                return (
                  <TableRow
                    hover
                    key={row.id}
                    selected={isSelected}
                    // onClick={() => handleSelectRow(row.id)}
                    sx={{
                      backgroundColor: isSelected
                        ? "rgba(156, 39, 176, 0.08) !important"
                        : "inherit",
                      "&:hover": {
                        backgroundColor: isSelected ? "" : "inherit !important",
                      },
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        backgroundColor: isSelected
                          ? "#f0def3"
                          : "background.paper",
                      }}
                    >
                      <Checkbox
                        color="secondary"
                        checked={isSelected}
                        onChange={() => handleSelectRow(row.id)}
                        // onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 52,
                        zIndex: 1,
                        whiteSpace: "normal",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                        backgroundColor: isSelected
                          ? "#f0def3"
                          : "background.paper",
                      }}
                    >
                      {row.search_term ? row.search_term : ""}
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 162,
                        zIndex: 1,
                        whiteSpace: "normal",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                        backgroundColor: isSelected
                          ? "#f0def3"
                          : "background.paper",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      {row.negative_term_match_type
                        ? row.negative_term_match_type
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {row.campaign_id ? row.campaign_id : "-"}
                    </TableCell>
                    <TableCell>
                      {row.campaign_name ? row.campaign_name : "-"}
                    </TableCell>
                    <TableCell>
                      {row.last_seen
                        ? dayjs(row.last_seen).format("DD/MM/YYYY")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {row.impression_last_3_years
                        ? formatNumber(row.impression_last_3_years)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {row.clicks_last_3_years
                        ? formatNumber(row.clicks_last_3_years)
                        : "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 2,
                        backgroundColor: isSelected
                          ? "#f0def3"
                          : "background.paper",
                        borderLeft: "1px solid #e0e0e0",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        // onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="Remove NST from Campaign">
                          <IconButton
                            disabled={!isSelected}
                            size="small"
                            color={isSelected ? "error" : "default"}
                            onClick={() => {
                              dispatch(setOpenBulkActionModalForCampaign(true));
                              dispatch(
                                setSingleRowIDPassToModalForCampaign(row.id)
                              );
                            }}
                          >
                            <RemoveCircleOutlineOutlinedIcon
                              data-tracker="506"
                              fontSize="small"
                            />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Remove NST from Campaign and Add to Ad Group">
                          <IconButton
                            data-tracker="509"
                            disabled={!isSelected}
                            size="small"
                            color={isSelected ? "success" : "default"}
                            onClick={() => {
                              // event.stopPropagation();
                              dispatch(setOpenMoveAnotherCampaignModal(true));
                              dispatch(
                                setSingleRowIDPassToModalForCampaign(row.id)
                              );
                            }}
                          >
                            <RepartitionOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeaders.length + 1} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination footer */}
      {paginatedRows.length > 0 && (
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 1 }}
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": { display: "none" },
              ".MuiTablePagination-toolbar": { paddingLeft: "16px" },
            }}
          />

          <Pagination
            count={Math.ceil(filteredRows.length / rowsPerPage)}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
            showFirstButton
            showLastButton
            color="secondary"
            size="small"
          />

          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-actions": { display: "none" },
              ".MuiTablePagination-input": { marginRight: "15px" },
            }}
          />
        </Stack>
      )}
    </>
  );
};

export default NegativesInCampaignTable;
